/**
 * API client — handles auth tokens, refresh, and base URL.
 */

const API_BASE = '/api'

let accessToken: string | null = null
let refreshToken: string | null = null

export function setTokens(access: string, refresh: string) {
  accessToken = access
  refreshToken = refresh
  localStorage.setItem('refreshToken', refresh)
}

export function clearTokens() {
  accessToken = null
  refreshToken = null
  localStorage.removeItem('refreshToken')
}

export function getStoredRefreshToken(): string | null {
  return refreshToken ?? localStorage.getItem('refreshToken')
}

export function hasAccessToken(): boolean {
  return accessToken !== null
}

async function tryRefresh(): Promise<boolean> {
  const rt = getStoredRefreshToken()
  if (!rt) return false

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    })

    if (!res.ok) {
      clearTokens()
      return false
    }

    const data = await res.json() as { accessToken: string }
    accessToken = data.accessToken
    return true
  } catch {
    clearTokens()
    return false
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<{ data?: T; error?: string; status: number }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  let res = await fetch(`${API_BASE}${path}`, { ...options, headers })

  // If 401 and we have a refresh token, try refresh once
  if (res.status === 401 && getStoredRefreshToken()) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      headers['Authorization'] = `Bearer ${accessToken}`
      res = await fetch(`${API_BASE}${path}`, { ...options, headers })
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string }
    return { error: body.error ?? 'Request failed', status: res.status }
  }

  const data = await res.json() as T
  return { data, status: res.status }
}
